/**
 * PURPOSE: Contains logic related to the application bootstrap process.
 */

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { payloadUtils } from '@makemydeal/dr-shared-ui-utils';

// consts/enums
import * as bootstrapActionTypes from '../actionTypes/bootstrapActionTypes';
import * as initActionTypes from '../actionTypes/initActionTypes';

// interfaces/types
import type { InitContext } from '../types/initTypes';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '../types/middlewareTypes';

// utils
import { addRemainingGlobalReducers, addRemainingScopedReducers } from '../store/configureStoreCommon';

export type ReducersObject = Record<string, any>;
export type AddRemainingReducers = (reducers: ReducersObject) => void;
export type GetRemainingReducers = (initContext: InitContext) => ReducersObject;

export type GetDocumentTitle = (state: any) => string;

export type BuildBootstrapMiddlewareOptions = {
    getGlobalRemainingReducers: GetRemainingReducers;
    getScopedRemainingReducers?: GetRemainingReducers;
    getDocumentTitle: GetDocumentTitle;
};

export const buildBootstrapMiddleware = <TypeStateTree>(options: BuildBootstrapMiddlewareOptions) => {
    const bootstrapMiddleware: ReduxMiddleware<TypeStateTree> =
        (store: ReduxStore<TypeStateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
            if (action.type === bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
                const { dealValidation, featureToggles } = action.payload;
                const initContext: InitContext = {
                    featureToggles: payloadUtils.getFeatureTogglesFromInitPayload(action.payload),
                    dealer: payloadUtils.getDealerFromInitPayload(action.payload)
                };

                const remainingGlobalReducers = options.getGlobalRemainingReducers(initContext);
                const remainingScopedReducers = options.getScopedRemainingReducers?.(initContext);
                addRemainingGlobalReducers(remainingGlobalReducers);
                remainingScopedReducers && addRemainingScopedReducers(remainingScopedReducers);
                // check if deal is invalid then dispatch INIT_PENCIL_INVALID_DEAL action
                if (featureToggles?.mvDealValidation && Object.keys(dealValidation ?? {}).length > 0 && !dealValidation.valid) {
                    store.dispatch({
                        type: initActionTypes.INIT_PENCIL_INVALID_DEAL,
                        payload: action.payload
                    });
                } else {
                    // otherwise dispatch INIT_PENCIL_SUCCESS action
                    store.dispatch({
                        type: initActionTypes.INIT_PENCIL_SUCCESS,
                        payload: action.payload
                    });
                }
                // UPDATE_DOCUMENT_TITLE lets apps that never dispatch INIT_SUCCESS (e.g. sales-checkin)
                // reuse this title logic without this shared middleware knowing their app-specific actions.
            } else if (action.type === initActionTypes.INIT_SUCCESS || action.type === initActionTypes.UPDATE_DOCUMENT_TITLE) {
                const state = store.getState();
                const documentTitle = options.getDocumentTitle(state);
                if (documentTitle) {
                    document.title = documentTitle;
                }
            }

            return next(action);
        };
    return bootstrapMiddleware;
};
